import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import { EmptyState, ErrorState, LoadingIndicator } from '../components/ui/States';
import { getSupplierStatement, SupplierStatement } from '../lib/supplierStatementApi';
import { downloadObjectsCsv } from '../utils/downloadCsv';
import { formatDateTime } from '../utils/dateFormat';
import { StatementControls } from '../features/supplierStatement/StatementControls';
import { StatementTable } from '../features/supplierStatement/StatementTable';
import { ReconciliationNotice } from '../features/supplierStatement/ReconciliationNotice';
import {
  CSV_HEADERS,
  formatAmount,
  periodLabel,
  statementCsvRows,
  statementFileName,
} from '../features/supplierStatement/statementDisplay';

/**
 * A supplier's statement of account (D49 phase 3): opening balance, every invoice, payment and
 * credit in date order, closing balance. What the business sends to, or reconciles against, a
 * supplier's own statement. Read-only. Printable (the browser's "Save as PDF" gives the file) and
 * exportable as CSV.
 *
 * Reached at /accounts/suppliers/:supplierName/statement, optionally with ?currency=USD&from=
 * yyyy-mm-dd&to=yyyy-mm-dd. Every figure is computed by the server; this page only lays it out.
 */

/** A yyyy-mm-dd from the address, or '' (a malformed one is ignored, not sent). */
function dayFromUrl(value: string | null): string {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
}

// Print: only the statement, no app chrome. The same approach as the payment voucher page
// (everything hidden, the statement's root made visible), so the two documents print alike.
const PRINT_CSS = `
  @page { size: A4 portrait; margin: 12mm; }
  @media print {
    body * { visibility: hidden !important; }
    .supplier-statement-root, .supplier-statement-root * { visibility: visible !important; }
    .supplier-statement-root {
      position: absolute; left: 0; top: 0; width: 100%; height: auto !important;
      overflow: visible !important; background: white !important;
    }
    .supplier-statement-document { border: 0 !important; box-shadow: none !important; padding: 0 !important; }
    .statement-table-wrap { overflow: visible !important; }
    .statement-table { min-width: 0 !important; width: 100% !important; font-size: 10px !important; }
    .statement-table th, .statement-table td { padding: 3px 4px !important; }
    .statement-table tr { break-inside: avoid; }
    .statement-table thead { display: table-header-group; }
    .statement-reconciliation, .statement-legend { break-inside: avoid; }
    .no-print { display: none !important; }
  }
`;

export const SupplierStatementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The router has already decoded the path segment. Decoding it again would throw on a supplier
  // name that contains a percent sign.
  const { supplierName = '' } = useParams();

  const [currency, setCurrency] = useState(() => (new URLSearchParams(location.search).get('currency') || '').toUpperCase());
  const [from, setFrom] = useState(() => dayFromUrl(new URLSearchParams(location.search).get('from')));
  const [to, setTo] = useState(() => dayFromUrl(new URLSearchParams(location.search).get('to')));
  // The last statement is kept while the next one loads, so the currency chooser and the dates do
  // not vanish from under the person changing them.
  const [statement, setStatement] = useState<SupplierStatement | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [notFound, setNotFound] = useState(false);
  const [reloads, setReloads] = useState(0);
  const latest = useRef(0);

  // The pair of dates the server refused (DATE_RANGE_INVALID). A blank box means "the server's
  // default", so From typed against a default To (today) can only be judged by the server; when it
  // refuses, the reason is said beside the boxes exactly as for two dates typed here, and nothing
  // more is asked until a date changes.
  const [refusedDates, setRefusedDates] = useState('');

  // The dates cannot be used: nothing is asked, and the reason is said beside the boxes.
  const rangeProblem =
    from && to && from > to
      ? 'The From date is after the To date. Change one of them.'
      : refusedDates === `${from}|${to}`
        ? 'These dates cannot be used. Check that From is not after To.'
        : '';

  useEffect(() => {
    if (!supplierName || rangeProblem) return;
    // Only the newest answer is kept: changing a date twice quickly must not show the first one.
    latest.current += 1;
    const mine = latest.current;
    setStatus('loading');
    getSupplierStatement({ supplier: supplierName, currency, from, to })
      .then((next) => {
        if (mine !== latest.current) return;
        setStatement(next);
        setStatus('ready');
      })
      .catch((err: { status?: number; code?: string }) => {
        if (mine !== latest.current) return;
        // The machine-readable code, never the server's prose.
        if (err?.code === 'DATE_RANGE_INVALID') {
          setRefusedDates(`${from}|${to}`);
          setStatus('ready');
          return;
        }
        setNotFound(err?.status === 404);
        setStatus('error');
      });
  }, [supplierName, currency, from, to, rangeProblem, reloads]);

  // Not while the dates are unusable: the document would carry a period nobody is asking for.
  const shown = status === 'ready' && !rangeProblem ? statement : null;
  const complete = Boolean(shown && !shown.currencyRequired);

  const exportCsv = useCallback(() => {
    if (!shown || shown.currencyRequired) return;
    downloadObjectsCsv(statementFileName(shown), statementCsvRows(shown), CSV_HEADERS);
  }, [shown]);

  const back = useMemo(
    () => (
      <Button
        variant="secondary"
        icon={<ArrowLeft aria-hidden="true" className="h-4 w-4" />}
        onClick={() => navigate(`/accounts/suppliers/${encodeURIComponent(supplierName)}`)}
      >
        Back
      </Button>
    ),
    [navigate, supplierName]
  );

  return (
    <div className="supplier-statement-root flex h-full flex-col overflow-auto bg-brand-bg">
      <style>{PRINT_CSS}</style>

      <div className="no-print">
        <PageHeader
          back={back}
          title="Statement of account"
          subtitle={`${supplierName || 'Supplier'}: what was invoiced, paid and credited, and what is owed.`}
          actions={
            <>
              <Button variant="secondary" icon={<Download aria-hidden="true" className="h-4 w-4" />} onClick={exportCsv} disabled={!complete}>
                Export CSV
              </Button>
              <Button variant="primary" icon={<Printer aria-hidden="true" className="h-4 w-4" />} onClick={() => window.print()} disabled={!complete}>
                Print
              </Button>
            </>
          }
        >
          <StatementControls
            currencies={statement?.currencies || []}
            currency={currency}
            needsCurrency={Boolean(shown?.currencyRequired)}
            onCurrencyChange={setCurrency}
            from={from || statement?.from || ''}
            to={to || statement?.to || ''}
            onFromChange={setFrom}
            onToChange={setTo}
            rangeProblem={rangeProblem}
          />
        </PageHeader>
      </div>

      <main className="flex-1 px-4 py-4 sm:px-8">
        {status === 'loading' && !rangeProblem ? (
          <div className="no-print">
            <LoadingIndicator label="Loading the statement..." />
          </div>
        ) : null}

        {status === 'error' ? (
          <div className="no-print">
            {notFound ? (
              <EmptyState
                title="There is no statement to show."
                detail="This supplier has no bills recorded, or none in the currency chosen."
              />
            ) : (
              <ErrorState message="The statement could not be loaded." onRetry={() => setReloads((n) => n + 1)} />
            )}
          </div>
        ) : null}

        {shown && shown.currencyRequired ? (
          <div className="no-print">
            <EmptyState
              title="This supplier has bills in more than one currency."
              detail="Choose a currency above. A statement is never added up across currencies."
            />
          </div>
        ) : null}

        {shown && !shown.currencyRequired ? (
          <article
            className="supplier-statement-document mx-auto max-w-6xl space-y-4 rounded-lg border border-slate-200 bg-white p-4 sm:p-6"
            aria-label="Statement of account"
          >
            <header className="space-y-1">
              <h2 className="text-base font-semibold leading-6 text-slate-900">Statement of account</h2>
              <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-0.5 text-sm text-slate-700">
                <dt className="font-medium text-slate-500">Supplier</dt>
                <dd className="font-semibold text-slate-900">{shown.supplierName}</dd>
                <dt className="font-medium text-slate-500">Currency</dt>
                <dd>{shown.currency}</dd>
                <dt className="font-medium text-slate-500">Period</dt>
                <dd>{periodLabel(shown.from, shown.to)}</dd>
                <dt className="font-medium text-slate-500">Generated</dt>
                <dd>{formatDateTime(shown.generatedAt, '--')}</dd>
              </dl>
            </header>

            <StatementTable statement={shown} onOpen={navigate} />

            {shown.totals ? (
              <dl className="statement-totals flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-700" data-testid="statement-totals">
                <div className="flex gap-1.5">
                  <dt className="text-slate-500">Invoiced</dt>
                  <dd className="font-medium tabular-nums">{formatAmount(shown.totals.invoiced)}</dd>
                </div>
                <div className="flex gap-1.5">
                  <dt className="text-slate-500">Paid</dt>
                  <dd className="font-medium tabular-nums">{formatAmount(shown.totals.paid)}</dd>
                </div>
                <div className="flex gap-1.5">
                  <dt className="text-slate-500">Credited</dt>
                  <dd className="font-medium tabular-nums">{formatAmount(shown.totals.credited)}</dd>
                </div>
                {shown.totals.adjustments !== 0 ? (
                  <div className="flex gap-1.5">
                    <dt className="text-slate-500">Marked paid, no payment record</dt>
                    <dd className="font-medium tabular-nums">{formatAmount(shown.totals.adjustments)}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}

            {shown.reconciliation ? (
              <ReconciliationNotice
                reconciliation={shown.reconciliation}
                closing={shown.closing}
                currency={shown.currency || ''}
              />
            ) : null}

            <p className="statement-legend text-xs text-slate-500">
              Balance is what is owed to the supplier after each line. A debit adds to it and a credit reduces it. A
              negative balance means the supplier owes us. A date marked estimated is not printed on the document; it is
              the day the record was made or last changed.
            </p>
          </article>
        ) : null}
      </main>
    </div>
  );
};

export default SupplierStatementPage;
